import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";

const OpenStreetAutocomplete = forwardRef(
  ({ onSelectLocation, placeholder }, ref) => {
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [selectedPlace, setSelectedPlace] = useState(null);

    // Fetch autocomplete results
    useEffect(() => {
      const fetchSuggestions = async () => {
        if (query.length < 3) {
          setSuggestions([]);
          return;
        }

        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          query,
        )}&format=json&addressdetails=1&limit=5&viewbox=-8.64,60.85,1.77,49.86&bounded=1`; // view box is set to united kingdom

        try {
          const response = await fetch(url);
          const data = await response.json();
          // Filter to show only city/locality-level places
          const filtered = data.filter((place) =>
            [
              "city",
              "town",
              "village",
              "suburb",
              "hamlet",
              "neighbourhood",
            ].includes(place.type),
          );

          setSuggestions(filtered.slice(0, 5)); // top 5 suggestions
          setSuggestions(data);
        } catch (err) {
          console.error("Failed to fetch suggestions:", err);
        }
      };

      if (query === "") {
        setSelectedPlace(null);
        if (onSelectLocation) {
          onSelectLocation({});
        }
      } else {
        if (!selectedPlace) {
          const timeout = setTimeout(fetchSuggestions, 300); // debounce
          return () => clearTimeout(timeout);
        }
      }
    }, [query]);

    // Handle selection
    const handleSelect = (place) => {
      setSelectedPlace(place);
      setQuery(place.display_name);
      setSuggestions([]);
      if (onSelectLocation) {
        onSelectLocation({
          address: place.display_name,
          lat: place.lat,
          lng: place.lon,
        });
      }
    };

    useImperativeHandle(ref, () => ({
      reset() {
        setQuery("");
        setSuggestions([]);
        setSelectedPlace(null);
      },
    }));

    return (
      <div className="relative w-full max-w-md">
        <input
          type="text"
          placeholder={placeholder || `Search a place`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded"
        />
        {suggestions.length > 0 && (
          <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded max-h-60 overflow-y-auto">
            {suggestions.map((place) => (
              <li
                key={place.place_id}
                onClick={() => handleSelect(place)}
                className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
              >
                {place.display_name}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
);

export default OpenStreetAutocomplete;
